import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Badge, Btn, Card, MetricCard, SectionHeader, DataTable, FormRow, Field, Modal, FormActions, Avatar, Pagination, Tabs } from '../../components/ui';
import { Icon } from '../../icons';
import { ConfirmModal } from '../../components/shared';
import { getUsers, getRoles, createUser, updateUser, deleteUser, getPermissionsMatrix, updatePermissionsMatrix, createRole, resetRolePermissions, resetUserPassword, resendUserInvite } from '../../api/users';
import { uploadFiles } from '../../api/files';
import { getErrorMessage, getFieldError } from '../../utils/apiUtils';
import { usePermissions } from '../../hooks/usePermissions';
import { toastError, toastSuccess } from '../../utils/toast';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function UsersScreen({ onNav }) {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.admin?.[key] || translations.en?.admin?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLastLogin, setFilterLastLogin] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [stats, setStats] = useState({ total: 0, active: 0, admins: 0 });
  const [adminLimit, setAdminLimit] = useState(-1);
  const [apiErrors, setApiErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // User Actions Modals
  const [resetPassUser, setResetPassUser] = useState(null);
  const [adminPassword, setAdminPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPassSaving, setResetPassSaving] = useState(false);
  const [resetPassErrors, setResetPassErrors] = useState(null);
  const [resendInviteUser, setResendInviteUser] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(false);

  // Permissions Matrix State
  const [matrixRoles, setMatrixRoles] = useState([]);
  const [matrixModules, setMatrixModules] = useState([]);
  const [matrixPermissions, setMatrixPermissions] = useState([]);
  const [matrixData, setMatrixData] = useState({});
  const [initialMatrixData, setInitialMatrixData] = useState({});
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [matrixSearch, setMatrixSearch] = useState("");
  const [hasRbacFeature, setHasRbacFeature] = useState(true);
  const [matrixAuditTrail, setMatrixAuditTrail] = useState([]);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [showConfirmSaveMatrixModal, setShowConfirmSaveMatrixModal] = useState(false);

  // Bulk Permission Updates
  const [bulkLevel, setBulkLevel] = useState("full_access");
  const [bulkTargetRole, setBulkTargetRole] = useState("");

  // Reports
  const [showLoginReportModal, setShowLoginReportModal] = useState(false);

  // Custom Role & Clone Modal State
  const [roleModal, setRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: "", description: "", clone_role_id: "" });
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleErrors, setRoleErrors] = useState(null);

  // Reset Role Permissions Confirmation Modal
  const [resetRoleTarget, setResetRoleTarget] = useState(null);
  const [resettingRole, setResettingRole] = useState(false);

  // Unsaved changes warning modal when clicking elsewhere
  const [showUnsavedWarningModal, setShowUnsavedWarningModal] = useState(false);
  const [pendingNavigationAction, setPendingNavigationAction] = useState(null);

  const canEditRBAC = hasPermission('users.update');
  const isDirty = JSON.stringify(matrixData) !== JSON.stringify(initialMatrixData);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (newTab) => {
    if (activeTab === 1 && newTab === 0 && isDirty) {
      setPendingNavigationAction(() => () => setActiveTab(newTab));
      setShowUnsavedWarningModal(true);
      return;
    }
    setActiveTab(newTab);
  };

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const def = { name: "", email: "", phone: "", role_id: "", is_active: true, password: "", avatar: "" };

  const fieldLabels = {
    name: t("label_full_name"),
    email: t("label_email"),
    phone: t("label_phone"),
    role_id: t("label_role"),
    password: t("label_password")
  };

  const tError = (field, msg) => {
    if (!msg) return null;
    let text = Array.isArray(msg) ? msg[0] : msg;
    if (typeof text !== 'string') return text;
    const lower = text.toLowerCase();
    if (lower.includes("required")) return t("val_required", { field: fieldLabels[field] || field });
    if (lower.includes("taken") || lower.includes("exists")) return t("val_taken", { field: fieldLabels[field] || field });
    if (lower.includes("invalid")) return t("val_invalid", { field: fieldLabels[field] || field });
    return text;
  };

  const getErr = (field) => {
    const err = getFieldError(apiErrors, field);
    return err ? tError(field, err) : null;
  };

  const [form, setForm] = useState(def);
  const sf = k => e => {
    setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    // Clear error for this field when user types
    if (apiErrors?.errors?.[k]) setApiErrors(prev => ({ ...prev, errors: { ...prev.errors, [k]: null } }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toastError(t("val_invalid_photo_type"));
      return;
    }

    // Validate size: 5MB
    if (file.size > 5 * 1024 * 1024) {
      toastError(t("val_photo_too_large"));
      return;
    }

    setPhotoPreview(URL.createObjectURL(file));
    setUploadingPhoto(true);
    try {
      const res = await uploadFiles(file, 'profile_images', 'public');
      const uploaded = res.data?.uploaded || res.uploaded || [];
      if (uploaded.length > 0) {
        setForm(f => ({ ...f, avatar: uploaded[0].file_url }));
      }
    } catch (err) {
      toastError(getErrorMessage(err, t("toast_photo_upload_failed")));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t("text_never_logged_in");
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return t("text_never_logged_in");
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ' ' + d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const fetchData = async (currentPage = page) => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: perPage,
        search: search || undefined,
        role_id: filterRole || undefined,
        is_active: filterStatus !== "" ? filterStatus : undefined,
        last_login: filterLastLogin || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      const [usersRes, rolesRes] = await Promise.all([getUsers(params), getRoles()]);
      const userList = usersRes.data || [];
      setUsers(userList);
      setTotalUsers(usersRes.meta?.total || userList.length);
      setRoles(rolesRes.data || []);

      // Find the most recent login among users
      const loginTimestamps = userList
        .map(u => u.last_login_at || u.last_login || u.last_activity_at)
        .filter(Boolean)
        .map(t => new Date(t).getTime())
        .filter(t => !isNaN(t));

      let lastLoginFormatted = t("text_never_logged_in");
      let lastLoginCompact = t("text_never_logged_in");
      let lastLoginTime = "";
      if (loginTimestamps.length > 0) {
        const latestTimestamp = Math.max(...loginTimestamps);
        const latestDate = new Date(latestTimestamp);
        const isToday = latestDate.toDateString() === new Date().toDateString();
        lastLoginCompact = isToday ? t("text_today", "Today") : latestDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short'
        });
        lastLoginTime = latestDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        lastLoginFormatted = latestDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }) + ' ' + lastLoginTime;
      }

      setStats({
        total: usersRes.meta?.total || userList.length,
        active: userList.filter(u => u.is_active).length,
        admins: userList.filter(u => u.roles?.some(r => r.slug !== 'member')).length,
        lastLogin: lastLoginFormatted,
        lastLoginCompact: lastLoginCompact,
        lastLoginTime: lastLoginTime
      });
      setAdminLimit(usersRes.meta?.admin_limit ?? -1);
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_load_users_failed")));
    } finally {
      setLoading(false);
    }
  };

  const fetchMatrixData = async () => {
    setMatrixLoading(true);
    try {
      const res = await getPermissionsMatrix();
      if (res.success && res.data) {
        setMatrixRoles(res.data.roles || []);
        setMatrixModules(res.data.modules || []);
        setMatrixPermissions(res.data.permissions || []);
        setMatrixData(res.data.matrix || {});
        setInitialMatrixData(JSON.parse(JSON.stringify(res.data.matrix || {})));
        setMatrixAuditTrail(res.data.audit_trail || []);
      }
    } catch (err) {
      if (err?.status === 403) {
        setHasRbacFeature(false);
      } else {
        console.error(getErrorMessage(err, t("toast_load_matrix_failed")));
      }
    } finally {
      setMatrixLoading(false);
    }
  };

  const handleCellChange = (mod, roleId, value) => {
    setMatrixData(prev => ({
      ...prev,
      [mod]: {
        ...prev[mod],
        [roleId]: value
      }
    }));
  };

  // Select all modules for a specific role
  const handleSelectAllForRole = (roleId, level = 'full_access') => {
    setMatrixData(prev => {
      const updated = { ...prev };
      matrixModules.forEach(mod => {
        if (!updated[mod]) updated[mod] = {};
        updated[mod][roleId] = level;
      });
      return updated;
    });
  };

  // Select all roles for a specific module
  const handleSelectAllForModule = (mod, level = 'full_access') => {
    setMatrixData(prev => {
      const updated = { ...prev };
      if (!updated[mod]) updated[mod] = {};
      matrixRoles.forEach(r => {
        if (r.slug !== 'super_admin') {
          updated[mod][r.id] = level;
        }
      });
      return updated;
    });
  };

  // Bulk update all modules across a selected role or all roles
  const handleBulkUpdate = (targetRoleId, level) => {
    setMatrixData(prev => {
      const updated = { ...prev };
      matrixModules.forEach(mod => {
        if (!updated[mod]) updated[mod] = {};
        matrixRoles.forEach(r => {
          if (r.slug !== 'super_admin' && (!targetRoleId || targetRoleId === String(r.id))) {
            updated[mod][r.id] = level;
          }
        });
      });
      return updated;
    });
    toastSuccess(t("toast_matrix_updated"));
  };

  const resetMatrix = () => {
    setMatrixData(JSON.parse(JSON.stringify(initialMatrixData)));
    toastSuccess(t("toast_changes_discarded"));
  };

  const executeSaveMatrix = async () => {
    setShowConfirmSaveMatrixModal(false);
    setMatrixSaving(true);
    try {
      const modifiedRoleIds = [];
      matrixRoles.forEach(role => {
        if (role.slug === 'super_admin') return;

        let isRoleDirty = false;
        matrixModules.forEach(mod => {
          if (matrixData[mod]?.[role.id] !== initialMatrixData[mod]?.[role.id]) {
            isRoleDirty = true;
          }
        });
        if (isRoleDirty) {
          modifiedRoleIds.push(role.id);
        }
      });

      if (modifiedRoleIds.length === 0) {
        toastSuccess(t("toast_no_changes"));
        return;
      }

      await Promise.all(modifiedRoleIds.map(async (roleId) => {
        const permissionsPayload = [];
        matrixPermissions.forEach(permission => {
          const mod = permission.module;
          const action = permission.action;
          const cellLevel = matrixData[mod]?.[roleId] || 'no_access';

          let level = 'no_access';
          if (cellLevel === 'full_access') {
            level = 'full_access';
          } else if (cellLevel === 'entry_access') {
            if (['view', 'create', 'update', 'upload', 'download'].includes(action)) {
              level = 'entry_access';
            }
          } else if (cellLevel === 'read_access') {
            if (['view', 'download'].includes(action)) {
              level = 'read_access';
            }
          }

          permissionsPayload.push({
            permission_id: permission.id,
            access_level: level
          });
        });

        await updatePermissionsMatrix({
          role_id: roleId,
          permissions: permissionsPayload
        });
      }));

      toastSuccess(t("toast_matrix_updated"));
      await fetchMatrixData();
    } catch (err) {
      toastError(getErrorMessage(err, t("toast_matrix_update_failed")));
    } finally {
      setMatrixSaving(false);
    }
  };

  const handleExportUsersPDF = async () => {
    try {
      // Fetch all users matching active filters
      const res = await getUsers({
        per_page: 500,
        search: search || undefined,
        role_id: filterRole || undefined,
        is_active: filterStatus !== "" ? filterStatus : undefined,
        last_login: filterLastLogin || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const exportList = res.data || users;

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Society Users Directory", 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()} | Total Records: ${exportList.length}`, 14, 22);

      const tableColumn = ["User ID", "Name", "Email", "Phone", "Role", "Status", "Created Date", "Last Active"];
      const tableRows = exportList.map(u => [
        `#${u.id}`,
        u.name || "-",
        u.email || "-",
        u.phone || "-",
        u.roles?.[0]?.name || "No Role",
        u.is_active ? "Active" : "Inactive",
        formatDateOnly(u.created_at),
        formatTimestamp(u.last_login_at || u.last_activity_at)
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 28,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [79, 70, 229] },
      });

      doc.save(`Users_List_${new Date().toISOString().slice(0, 10)}.pdf`);
      toastSuccess(t("toast_report_exported", "PDF report downloaded successfully!"));
    } catch (err) {
      toastError(getErrorMessage(err, "Failed to generate PDF"));
    }
  };

  const handleExportUsersExcel = async () => {
    try {
      const res = await getUsers({
        per_page: 500,
        search: search || undefined,
        role_id: filterRole || undefined,
        is_active: filterStatus !== "" ? filterStatus : undefined,
        last_login: filterLastLogin || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const exportList = res.data || users;

      const headers = ["User ID", "Name", "Email", "Phone", "Role", "Status", "Created Date", "Last Active"];
      const rows = exportList.map(u => [
        `#${u.id}`,
        u.name || "",
        u.email || "",
        u.phone || "",
        u.roles?.[0]?.name || "No Role",
        u.is_active ? "Active" : "Inactive",
        formatDateOnly(u.created_at),
        formatTimestamp(u.last_login_at || u.last_activity_at)
      ]);

      const data = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Auto-fit & generous column widths so Excel never shows ######
      worksheet['!cols'] = [
        { wch: 12 }, // User ID
        { wch: 22 }, // Name
        { wch: 30 }, // Email
        { wch: 18 }, // Phone
        { wch: 18 }, // Role
        { wch: 14 }, // Status
        { wch: 18 }, // Created Date
        { wch: 24 }, // Last Active
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users Directory");

      // Generate real binary XLSX array
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Users_Directory_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toastSuccess(t("toast_report_exported", "Excel report downloaded successfully!"));
    } catch (err) {
      toastError(getErrorMessage(err, "Failed to export Excel"));
    }
  };

  const handleExportMatrix = () => {
    const headers = ["Module", ...matrixRoles.map(r => r.name)];
    const rows = matrixModules.map(mod => [
      mod,
      ...matrixRoles.map(role => role.slug === 'super_admin' ? 'full_access' : (matrixData[mod]?.[role.id] || 'no_access'))
    ]);
    const csvString = [headers.join(","), ...rows.map(e => e.join(","))].join("\r\n");
    const blob = new Blob(["\uFEFF" + csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Permissions_Matrix_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toastSuccess(t("toast_report_exported", "Permissions matrix exported successfully!"));
  };

  const getResetPassErr = (field) => {
    if (!resetPassErrors) return null;
    const err = getFieldError(resetPassErrors, field);
    if (err) return err;
    if (resetPassErrors?.errors?.[field]) {
      const val = resetPassErrors.errors[field];
      return Array.isArray(val) ? val[0] : val;
    }
    return null;
  };

  const handleResetPassword = async () => {
    if (!resetPassUser) return;

    const errors = {};
    if (!adminPassword || !adminPassword.trim()) {
      errors.admin_password = t("val_admin_password_required", "Please enter your administrator password.");
    }
    if (!newPassword) {
      errors.password = t("val_password_required", "New password is required.");
    } else if (newPassword.length < 6) {
      errors.password = t("val_password_min_6", "New password must be at least 6 characters.");
    }
    if (!confirmPassword) {
      errors.password_confirmation = t("val_confirm_password_required", "Please confirm the new password.");
    } else if (newPassword !== confirmPassword) {
      errors.password_confirmation = t("val_password_mismatch", "New password and confirmation do not match.");
    }

    if (Object.keys(errors).length > 0) {
      setResetPassErrors({ errors });
      const firstError = Object.values(errors)[0];
      toastError(firstError);
      return;
    }

    setResetPassErrors(null);
    setResetPassSaving(true);
    try {
      await resetUserPassword(resetPassUser.id, {
        admin_password: adminPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      toastSuccess(t("toast_password_reset_success"));
      setResetPassUser(null);
      setAdminPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setResetPassErrors(err);
      toastError(getErrorMessage(err, t("toast_password_reset_failed")));
    } finally {
      setResetPassSaving(false);
    }
  };

  const handleResendInvite = async () => {
    if (!resendInviteUser) return;
    setResendingInvite(true);
    try {
      await resendUserInvite(resendInviteUser.id);
      toastSuccess(t("toast_invite_sent"));
      setResendInviteUser(null);
    } catch (err) {
      toastError(getErrorMessage(err, t("toast_invite_failed")));
    } finally {
      setResendingInvite(false);
    }
  };

  const saveRole = async () => {
    setRoleErrors(null);
    setRoleSaving(true);
    try {
      await createRole({
        name: roleForm.name,
        description: roleForm.description,
        clone_role_id: roleForm.clone_role_id || null,
      });
      setRoleModal(false);
      setRoleForm({ name: "", description: "", clone_role_id: "" });
      toastSuccess(t("toast_role_created"));
      await Promise.all([fetchData(), fetchMatrixData()]);
    } catch (err) {
      setRoleErrors(err);
      toastError(getErrorMessage(err, t("toast_create_role_failed")));
    } finally {
      setRoleSaving(false);
    }
  };

  const handleResetRole = async () => {
    if (!resetRoleTarget) return;
    setResettingRole(true);
    try {
      await resetRolePermissions(resetRoleTarget.id);
      toastSuccess(t("toast_role_reset"));
      setResetRoleTarget(null);
      await fetchMatrixData();
    } catch (err) {
      toastError(getErrorMessage(err, t("toast_reset_role_failed")));
    } finally {
      setResettingRole(false);
    }
  };

  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    fetchData();
    fetchMatrixData();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = t("msg_unsaved_changes_leave");
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const save = async () => {
    setApiErrors(null);
    setIsSaving(true);
    try {
      if (editUser) {
        await updateUser(editUser.id, form);
      } else {
        await createUser(form);
      }
      const isEdit = Boolean(editUser);
      setModal(false);
      setEditUser(null);
      setForm(def);
      fetchData();
      toastSuccess(isEdit ? t("toast_user_updated") : t("toast_user_created"));
    } catch (err) {
      setApiErrors(err);
      if (err.response?.status === 403) {
        toastError(err.response?.data?.message || "Permission denied.");
      } else if (!err.errors) {
        toastError(getErrorMessage(err, t("toast_save_user_failed")));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = u => {
    setEditUser(u);
    setForm({
      name: u.name,
      email: u.email,
      phone: u.phone || "",
      role_id: u.roles?.[0]?.id || "",
      is_active: !!u.is_active,
      password: ""
    });
    setModal(true);
  };

  const toggleStatus = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      fetchData();
    } catch (err) {
      toastError(t("toast_update_status_failed"));
    }
  };

  const confirmDel = async () => {
    try {
      await deleteUser(delUser.id);
      setDelUser(null);
      fetchData();
      toastSuccess(t("toast_user_deleted"));
    } catch (err) {
      toastError(getErrorMessage(err, t("toast_delete_user_failed")));
    }
  };

  const roleOptions = roles.map(r => ({ value: r.id, label: r.name }));

  return (
    <>
      <div className="fade-up space-y-4">
        {delUser && <ConfirmModal title={t("title_remove_user")} message={t("msg_remove_user", { name: delUser.name })} onConfirm={confirmDel} onCancel={() => setDelUser(null)} />}

        {modal && (
          <Modal title={editUser ? t("title_edit_user") : t("title_add_new_user")} onClose={() => {
            setModal(false);
            setEditUser(null);
            setApiErrors(null);
            setPhotoPreview(null);
          }}>
            {apiErrors && apiErrors.errors && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 text-sm rounded-lg border border-red-100 dark:border-red-800">
                <ul className="list-disc list-inside">
                  {Object.entries(apiErrors.errors).map(([field, msgs]) => (
                    <li key={field}> {field}: {Array.isArray(msgs) ? msgs.join(', ') : msgs} </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Profile Photo Uploader */}
            <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60">
              <div className="relative flex-shrink-0">
                <Avatar name={form.name || "User"} src={photoPreview || form.avatar} size={54} />
                {uploadingPhoto && (
                  <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-ink dark:text-slate-200 mb-1">{t("label_profile_photo")}</div>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-ink dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80 cursor-pointer shadow-xs transition-colors">
                  <Icon name="upload" size={13} className="text-muted" />
                  <span>{uploadingPhoto ? "Uploading..." : "Upload Photo"}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handlePhotoChange}
                    disabled={uploadingPhoto}
                  />
                </label>
                <div className="text-[10px] text-muted dark:text-slate-400 mt-1">{t("placeholder_upload_photo")}</div>
              </div>
            </div>

            <FormRow cols={2}>
              <Field label={t("label_full_name")} value={form.name} onChange={sf("name")} required error={getErr('name')} />
              <Field label={t("label_email")} type="email" value={form.email} onChange={sf("email")} required error={getErr('email')} />
            </FormRow>
            <FormRow cols={2}>
              <Field label={t("label_phone")} value={form.phone} onChange={sf("phone")} error={getErr('phone')} />
              <Field label={t("label_role")} type="select" value={form.role_id} onChange={sf("role_id")} options={roleOptions} required error={getErr('role_id')} />
            </FormRow>
            {!editUser && (
              <FormRow>
                <Field label={t("label_password")} type="password" value={form.password} onChange={sf("password")} required error={getErr('password')} />
              </FormRow>
            )}
            <FormRow>
              <div className="flex items-center gap-2 px-1">
                <input type="checkbox" id="is_active" checked={form.is_active} onChange={sf("is_active")} className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-600" />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700 dark:text-slate-300">{t("label_account_active")}</label>
              </div>
            </FormRow>
            <FormActions
              onSave={save}
              onCancel={() => {
                setModal(false);
                setEditUser(null);
                setApiErrors(null);
                setPhotoPreview(null);
              }}
              label={editUser ? t("btn_update_user") : t("btn_add_user")}
              loading={isSaving || uploadingPhoto}
            />
          </Modal>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard label={t("metric_total_users")} value={stats.total} icon="users" accent="blue" loading={loading} />
          <MetricCard label={t("metric_active")} value={stats.active} icon="check" accent="green" loading={loading} />
          <MetricCard label={t("metric_super_admins")} value={stats.admins} icon="shield" accent="purple" loading={loading} />
          <MetricCard label={t("metric_last_login")} value={stats.lastLoginCompact || t("text_never_logged_in")} sublabel={stats.lastLoginTime} icon="activity" accent="amber" loading={loading} />
        </div>

        {/* Section Tabs */}
        <div className="pt-1">
          <Tabs
            tabs={[
              { id: 0, label: `${t("title_system_users")} (${totalUsers || users.length})` },
              { id: 1, label: t("title_rbac_matrix") }
            ]}
            active={activeTab}
            onChange={handleTabChange}
            scrollable
          />
        </div>

        {/* Reset Password Modal */}
        {resetPassUser && (
          <Modal
            title={t("title_reset_password_modal")}
            onClose={() => {
              setResetPassUser(null);
              setAdminPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setResetPassErrors(null);
            }}
          >
            <div className="mb-4 text-xs text-muted dark:text-slate-400 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60">
              Resetting password for <strong className="text-ink dark:text-slate-100">{resetPassUser.name}</strong> (<span className="font-mono">{resetPassUser.email}</span>).
              <div className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400">
                🔒 Security verification: Please enter your administrator password to authorize this action.
              </div>
            </div>

            {resetPassErrors && resetPassErrors.errors && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 text-sm rounded-lg border border-red-100 dark:border-red-800">
                <ul className="list-disc list-inside">
                  {Object.entries(resetPassErrors.errors).map(([field, msgs]) => (
                    <li key={field}> {field}: {Array.isArray(msgs) ? msgs.join(', ') : msgs} </li>
                  ))}
                </ul>
              </div>
            )}

            <FormRow>
              <Field
                label={t("label_admin_current_password")}
                type="password"
                value={adminPassword}
                onChange={e => {
                  setAdminPassword(e.target.value);
                  if (resetPassErrors?.errors?.admin_password || resetPassErrors?.admin_password) {
                    setResetPassErrors(prev => {
                      if (!prev) return null;
                      const next = { ...(prev.errors || {}) };
                      delete next.admin_password;
                      return Object.keys(next).length > 0 ? { ...prev, errors: next } : null;
                    });
                  }
                }}
                required
                error={getResetPassErr('admin_password')}
                placeholder="Enter your admin current password"
              />
            </FormRow>
            <FormRow cols={2}>
              <Field
                label={t("label_new_password")}
                type="password"
                value={newPassword}
                onChange={e => {
                  setNewPassword(e.target.value);
                  if (resetPassErrors?.errors?.password || resetPassErrors?.password) {
                    setResetPassErrors(prev => {
                      if (!prev) return null;
                      const next = { ...(prev.errors || {}) };
                      delete next.password;
                      return Object.keys(next).length > 0 ? { ...prev, errors: next } : null;
                    });
                  }
                }}
                required
                error={getResetPassErr('password')}
                placeholder="Enter new password (min 6 chars)"
              />
              <Field
                label={t("label_confirm_new_password")}
                type="password"
                value={confirmPassword}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  if (resetPassErrors?.errors?.password_confirmation || resetPassErrors?.password_confirmation) {
                    setResetPassErrors(prev => {
                      if (!prev) return null;
                      const next = { ...(prev.errors || {}) };
                      delete next.password_confirmation;
                      return Object.keys(next).length > 0 ? { ...prev, errors: next } : null;
                    });
                  }
                }}
                required
                error={getResetPassErr('password_confirmation')}
                placeholder="Re-enter new password"
              />
            </FormRow>
            <FormActions
              onSave={handleResetPassword}
              onCancel={() => {
                setResetPassUser(null);
                setAdminPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setResetPassErrors(null);
              }}
              label={t("btn_reset_password")}
              loading={resetPassSaving}
            />
          </Modal>
        )}

        {/* Resend Invite Confirmation Modal */}
        {resendInviteUser && (
          <ConfirmModal
            title={t("btn_resend_invite")}
            message={t("msg_confirm_resend_invite", { email: resendInviteUser.email })}
            onConfirm={handleResendInvite}
            onCancel={() => setResendInviteUser(null)}
            confirmText={t("btn_resend_invite")}
            loading={resendingInvite}
          />
        )}

        {/* User Login Activity Report Modal */}
        {showLoginReportModal && (
          <Modal title={t("title_login_activity_report")} onClose={() => setShowLoginReportModal(false)}>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              <div className="text-xs text-muted mb-2">Most recent user logins across the society.</div>
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2.5 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={u.name} src={u.avatar} size={32} />
                    <div>
                      <div className="text-xs font-bold text-ink dark:text-slate-200">{u.name}</div>
                      <div className="text-[10px] text-muted">{u.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-ink dark:text-slate-200">
                      {formatTimestamp(u.last_login_at || u.last_activity_at)}
                    </div>
                    <Badge color={u.is_active ? "green" : "gray"} dot sm>
                      {u.is_active ? t("status_active") : t("status_inactive")}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
              <Btn sm variant="secondary" icon="download" onClick={handleExportUsersPDF}>{t("btn_export_users_pdf")}</Btn>
              <Btn sm variant="secondary" icon="download" onClick={handleExportUsersExcel}>{t("btn_export_users_excel")}</Btn>
              <Btn sm variant="primary" onClick={() => setShowLoginReportModal(false)}>Close</Btn>
            </div>
          </Modal>
        )}

        {activeTab === 0 && (
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b dark:border-slate-800/60 pb-3 mb-4 gap-3">
            <div>
              <h3 className="text-base font-bold text-ink dark:text-white">{t("title_system_users")}</h3>
              <p className="text-xs text-muted dark:text-slate-400 mt-0.5">
                {totalUsers || users.length} {t("metric_total_users").toLowerCase()} registered
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Btn sm variant="secondary" icon="activity" onClick={() => setShowLoginReportModal(true)}>{t("btn_login_report")}</Btn>
              <Btn sm variant="secondary" icon="download" onClick={handleExportUsersPDF} className="hidden sm:inline-flex">{t("btn_export_users_pdf")}</Btn>
              <Btn sm variant="secondary" icon="download" onClick={handleExportUsersExcel}>{t("btn_export_users_excel")}</Btn>
              {hasPermission('users.create') && (
                adminLimit !== -1 && stats.admins >= adminLimit ? (
                  <Btn sm variant="primary" icon="plus" onClick={() => toastError(`Admin limit reached (${stats.admins}/${adminLimit}). Please upgrade your plan.`)} className="opacity-50 grayscale hidden lg:inline-flex">{t("btn_add_user")}</Btn>
                ) : (
                  <Btn sm variant="primary" icon="plus" onClick={() => { setEditUser(null); setPhotoPreview(null); setForm(def); setModal(true); }} className="hidden lg:inline-flex">{t("btn_add_user")}</Btn>
                )
              )}
            </div>
          </div>

          {/* Search, Filter & Quick Filter Bar */}
          <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800 mb-4 space-y-2.5">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Icon name="search" size={14} />
              </div>
              <input
                type="text"
                placeholder={t("placeholder_search_users")}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="w-full text-xs pl-8 pr-8 py-2.5 border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setPage(1); }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-transparent border-0 cursor-pointer"
                >
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <select
                value={filterRole}
                onChange={e => { setFilterRole(e.target.value); setPage(1); }}
                className="text-xs border border-gray-200 dark:border-slate-700 rounded-xl px-2.5 py-2 bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs truncate"
              >
                <option value="">{t("text_all_roles")}</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                className="text-xs border border-gray-200 dark:border-slate-700 rounded-xl px-2.5 py-2 bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs truncate"
              >
                <option value="">{t("text_all_status")}</option>
                <option value="true">{t("status_active")}</option>
                <option value="false">{t("status_inactive")}</option>
              </select>

              <div className="col-span-2 sm:col-span-1 flex gap-1.5">
                <select
                  value={filterLastLogin}
                  onChange={e => { setFilterLastLogin(e.target.value); setPage(1); }}
                  className="flex-1 text-xs border border-gray-200 dark:border-slate-700 rounded-xl px-2.5 py-2 bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs truncate"
                >
                  <option value="">{t("text_all_logins")}</option>
                  <option value="today">{t("opt_logged_today")}</option>
                  <option value="week">{t("opt_logged_this_week")}</option>
                  <option value="month">{t("opt_logged_this_month")}</option>
                  <option value="never">{t("opt_never_logged_in")}</option>
                </select>

                {(filterRole || filterStatus || filterLastLogin || search) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setFilterRole("");
                      setFilterStatus("");
                      setFilterLastLogin("");
                      setPage(1);
                    }}
                    title="Clear filters"
                    className="px-2.5 py-2 text-xs rounded-xl bg-slate-200/70 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium transition-colors border-0 cursor-pointer flex items-center justify-center flex-shrink-0"
                  >
                    <Icon name="refresh" size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <DataTable
              loading={loading}
              cols={[
                t("col_user_id"),
                <button
                  type="button"
                  onClick={() => {
                    if (sortBy === 'name') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('name'); setSortOrder('asc'); }
                  }}
                  className="font-bold flex items-center gap-1 hover:text-indigo-600 cursor-pointer bg-transparent border-0 p-0 text-inherit"
                >
                  <span>{t("col_user")}</span>
                  {sortBy === 'name' && <Icon name={sortOrder === 'asc' ? 'arrow_up' : 'arrow_down'} size={12} />}
                </button>,
                <button
                  type="button"
                  onClick={() => {
                    if (sortBy === 'role') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('role'); setSortOrder('asc'); }
                  }}
                  className="font-bold flex items-center gap-1 hover:text-indigo-600 cursor-pointer bg-transparent border-0 p-0 text-inherit"
                >
                  <span>{t("col_role")}</span>
                  {sortBy === 'role' && <Icon name={sortOrder === 'asc' ? 'arrow_up' : 'arrow_down'} size={12} />}
                </button>,
                t("col_status"),
                <button
                  type="button"
                  onClick={() => {
                    if (sortBy === 'created_at') setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    else { setSortBy('created_at'); setSortOrder('desc'); }
                  }}
                  className="font-bold flex items-center gap-1 hover:text-indigo-600 cursor-pointer bg-transparent border-0 p-0 text-inherit"
                >
                  <span>{t("col_created_at")}</span>
                  {sortBy === 'created_at' && <Icon name={sortOrder === 'asc' ? 'arrow_up' : 'arrow_down'} size={12} />}
                </button>,
                t("col_last_active"),
                t("col_actions")
              ]}
              rows={users.map(u => [
                <span className="font-mono text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                  #{u.id}
                </span>,
                <div className="flex items-center gap-2.5">
                  <Avatar name={u.name} src={u.avatar} size={32} />
                  <div>
                    <div className="text-sm font-semibold text-ink dark:text-slate-100">{u.name}</div>
                    <div className="text-sm text-muted dark:text-slate-400">{u.email}</div>
                  </div>
                </div>,
                <Badge color={u.roles?.[0]?.slug === 'super_admin' ? 'blue' : u.roles?.[0]?.slug === 'admin' ? 'amber' : 'gray'}>
                  {u.roles?.[0]?.name || t("text_no_role")}
                </Badge>,
                <Badge color={u.is_active ? "green" : "gray"} dot>{u.is_active ? t("status_active") : t("status_inactive")}</Badge>,
                <span className="text-xs text-muted dark:text-slate-400 font-medium">
                  {formatDateOnly(u.created_at)}
                </span>,
                <span className="text-xs text-muted dark:text-slate-400 font-medium">
                  {formatTimestamp(u.last_login_at || u.last_activity_at)}
                </span>,
                <div className="flex items-center gap-2 py-0.5 min-w-[210px]">
                  {hasPermission('users.update') && <Btn xs icon="edit" onClick={() => startEdit(u)} title={t("btn_edit")}>{t("btn_edit")}</Btn>}
                  {hasPermission('users.update') && (
                    <Btn
                      xs
                      variant={u.is_active ? "danger" : "success"}
                      onClick={() => toggleStatus(u)}
                      title={u.is_active ? t("btn_disable") : t("btn_enable")}
                    >
                      {u.is_active ? t("btn_disable") : t("btn_enable")}
                    </Btn>
                  )}
                  {hasPermission('users.update') && (
                    <Btn xs variant="secondary" icon="lock" onClick={() => setResetPassUser(u)} title={t("btn_reset_password")} />
                  )}
                  {hasPermission('users.create') && (
                    <Btn xs variant="secondary" icon="mail" onClick={() => setResendInviteUser(u)} title={t("btn_resend_invite")} />
                  )}
                  {hasPermission('users.delete') && <Btn xs variant="danger" icon="trash" onClick={() => setDelUser(u)} title={t("btn_del")}>{t("btn_del")}</Btn>}
                </div>
              ])}
            />
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {users.length === 0 && !loading && (
              <div className="text-center py-12 px-4 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3 text-slate-400">
                  <Icon name="users" size={24} />
                </div>
                <p className="text-sm font-semibold text-ink dark:text-white mb-1">{t("msg_no_users", "No users found")}</p>
                <p className="text-xs text-muted dark:text-slate-400">Try adjusting your search terms or filters.</p>
              </div>
            )}
            {loading && (
              <div className="py-12 text-center text-muted dark:text-slate-400 flex flex-col items-center justify-center gap-2">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs">{t("msg_loading")}</span>
              </div>
            )}
            {!loading && users.map(u => (
              <div
                key={u.id}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-3.5 shadow-xs transition-all hover:border-slate-300 dark:hover:border-slate-700"
              >
                {/* Top Section: Avatar, Details, Role */}
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <Avatar name={u.name} src={u.avatar} size={44} className="rounded-full shadow-xs" />
                    <span
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${
                        u.is_active ? 'bg-emerald-500' : 'bg-slate-400'
                      }`}
                      title={u.is_active ? t("status_active") : t("status_inactive")}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1.5 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded flex-shrink-0">
                          #{u.id}
                        </span>
                        <h4 className="text-sm font-bold text-ink dark:text-white truncate">
                          {u.name}
                        </h4>
                      </div>
                      <Badge
                        color={u.roles?.[0]?.slug === 'super_admin' ? 'blue' : u.roles?.[0]?.slug === 'admin' ? 'amber' : 'gray'}
                        sm
                      >
                        {u.roles?.[0]?.name || t("text_no_role")}
                      </Badge>
                    </div>

                    <div className="text-xs text-muted dark:text-slate-400 truncate mb-0.5 flex items-center gap-1.5">
                      <Icon name="mail" size={12} className="text-slate-400 flex-shrink-0" />
                      <span className="truncate">{u.email}</span>
                    </div>

                    {u.phone && (
                      <div className="text-xs text-muted dark:text-slate-400 truncate flex items-center gap-1.5">
                        <Icon name="phone" size={12} className="text-slate-400 flex-shrink-0" />
                        <a href={`tel:${u.phone}`} className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                          {u.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata Row */}
                <div className="grid grid-cols-2 gap-2 mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800/80 text-[11px] text-muted dark:text-slate-400">
                  <div className="flex items-center gap-1.5 truncate">
                    <Icon name="calendar" size={12} className="text-slate-400 flex-shrink-0" />
                    <span className="truncate">Joined: <strong className="font-semibold text-slate-700 dark:text-slate-300">{formatDateOnly(u.created_at)}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 justify-end truncate">
                    <Icon name="activity" size={12} className="text-slate-400 flex-shrink-0" />
                    <span className="truncate">Active: <strong className="font-semibold text-slate-700 dark:text-slate-300">{formatTimestamp(u.last_login_at || u.last_activity_at)}</strong></span>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between gap-1.5 mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    {hasPermission('users.update') && (
                      <button
                        type="button"
                        onClick={() => startEdit(u)}
                        className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors border-0 cursor-pointer active:scale-95"
                      >
                        <Icon name="edit" size={12} />
                        <span>{t("btn_edit")}</span>
                      </button>
                    )}

                    {hasPermission('users.update') && (
                      <button
                        type="button"
                        onClick={() => toggleStatus(u)}
                        className={`flex-1 inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-xs font-semibold transition-colors border-0 cursor-pointer active:scale-95 ${
                          u.is_active
                            ? "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                            : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                        }`}
                      >
                        <Icon name={u.is_active ? "x" : "check"} size={12} />
                        <span>{u.is_active ? t("btn_disable") : t("btn_enable")}</span>
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {hasPermission('users.update') && (
                      <button
                        type="button"
                        onClick={() => setResetPassUser(u)}
                        title={t("btn_reset_password")}
                        className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border-0 cursor-pointer active:scale-95"
                      >
                        <Icon name="lock" size={13} />
                      </button>
                    )}

                    {hasPermission('users.create') && (
                      <button
                        type="button"
                        onClick={() => setResendInviteUser(u)}
                        title={t("btn_resend_invite")}
                        className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border-0 cursor-pointer active:scale-95"
                      >
                        <Icon name="mail" size={13} />
                      </button>
                    )}

                    {hasPermission('users.delete') && (
                      <button
                        type="button"
                        onClick={() => setDelUser(u)}
                        title={t("btn_del")}
                        className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/60 border-0 cursor-pointer active:scale-95"
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            page={page}
            total={totalUsers}
            perPage={perPage}
            onPage={p => { setPage(p); fetchData(p); }}
          />
        </Card>
        )}

        {/* Custom Role Creation & Cloning Modal */}
        {roleModal && (
          <Modal title={t("title_create_custom_role")} onClose={() => {
            setRoleModal(false);
            setRoleForm({ name: "", description: "", clone_role_id: "" });
            setRoleErrors(null);
          }}>
            {roleErrors && roleErrors.errors && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 text-sm rounded-lg border border-red-100 dark:border-red-800">
                <ul className="list-disc list-inside">
                  {Object.entries(roleErrors.errors).map(([field, msgs]) => (
                    <li key={field}> {field}: {Array.isArray(msgs) ? msgs.join(', ') : msgs} </li>
                  ))}
                </ul>
              </div>
            )}
            <FormRow>
              <Field
                label={t("label_role_name")}
                value={roleForm.name}
                onChange={e => setRoleForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="e.g. Facility Manager, Security Supervisor"
              />
            </FormRow>
            <FormRow>
              <Field
                label={t("label_role_description")}
                value={roleForm.description}
                onChange={e => setRoleForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the role responsibilities"
              />
            </FormRow>
            <FormRow>
              <Field
                label={t("label_clone_permissions_from")}
                type="select"
                value={roleForm.clone_role_id}
                onChange={e => setRoleForm(f => ({ ...f, clone_role_id: e.target.value }))}
                options={[
                  { value: "", label: t("opt_select_role_clone") },
                  ...matrixRoles.filter(r => r.slug !== 'super_admin').map(r => ({ value: r.id, label: r.name }))
                ]}
              />
            </FormRow>
            <FormActions
              onSave={saveRole}
              onCancel={() => {
                setRoleModal(false);
                setRoleForm({ name: "", description: "", clone_role_id: "" });
                setRoleErrors(null);
              }}
              label={t("btn_create_role")}
              loading={roleSaving}
            />
          </Modal>
        )}

        {/* Reset Role Permissions Confirmation Modal */}
        {resetRoleTarget && (
          <ConfirmModal
            title={t("title_reset_role_perms")}
            message={t("msg_confirm_reset_role", { role: resetRoleTarget.name })}
            onConfirm={handleResetRole}
            onCancel={() => setResetRoleTarget(null)}
            confirmText={t("btn_reset_defaults")}
            loading={resettingRole}
          />
        )}

        {/* Confirm Save Matrix Modal */}
        {showConfirmSaveMatrixModal && (
          <ConfirmModal
            title={t("title_confirm_save_matrix")}
            message={t("msg_confirm_save_matrix")}
            onConfirm={executeSaveMatrix}
            onCancel={() => setShowConfirmSaveMatrixModal(false)}
            confirmText={t("btn_save_changes")}
            loading={matrixSaving}
          />
        )}

        {/* Audit Trail Modal */}
        {showAuditTrailModal && (
          <Modal title={t("title_audit_trail")} onClose={() => setShowAuditTrailModal(false)}>
            <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
              {matrixAuditTrail.length === 0 ? (
                <div className="text-xs text-muted text-center py-6">No recent permission changes logged.</div>
              ) : (
                matrixAuditTrail.map(log => (
                  <div key={log.id} className="p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-ink dark:text-slate-200">{log.user_name || "Admin"}</span>
                      <span className="text-[10px] text-muted">{formatTimestamp(log.created_at)}</span>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300">{log.description}</div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-end">
              <Btn sm variant="primary" onClick={() => setShowAuditTrailModal(false)}>Close</Btn>
            </div>
          </Modal>
        )}

        {/* Unsaved Changes In-App Warning Modal */}
        {showUnsavedWarningModal && (
          <ConfirmModal
            title={t("badge_unsaved_changes")}
            message={t("msg_unsaved_warning_modal")}
            onConfirm={() => {
              setShowUnsavedWarningModal(false);
              resetMatrix();
              if (pendingNavigationAction) pendingNavigationAction();
            }}
            onCancel={() => {
              setShowUnsavedWarningModal(false);
              setPendingNavigationAction(null);
            }}
            confirmText={t("btn_discard")}
            cancelText={t("btn_save_changes")}
          />
        )}

        {/* RBAC Matrix Card */}
        {activeTab === 1 && (
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b dark:border-slate-800/60 pb-3 mb-4 gap-3">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-ink dark:text-white">{t("title_rbac_matrix")}</h3>
              {isDirty && hasRbacFeature && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-full font-semibold border border-amber-300 dark:border-amber-700/50 animate-pulse">
                  {t("badge_unsaved_changes")}
                </span>
              )}
            </div>

            {hasRbacFeature && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 sm:flex-none">
                  <input
                    type="text"
                    placeholder={t("placeholder_search_modules")}
                    value={matrixSearch}
                    onChange={(e) => setMatrixSearch(e.target.value)}
                    className="text-xs border border-gray-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 w-full sm:w-36 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>

                <Btn sm variant="secondary" icon="activity" onClick={() => setShowAuditTrailModal(true)}>{t("title_audit_trail")}</Btn>
                <Btn sm variant="secondary" icon="download" onClick={handleExportMatrix}>{t("btn_export_matrix")}</Btn>

                {canEditRBAC && (
                  <Btn
                    sm
                    variant="outline_primary"
                    icon="plus"
                    onClick={() => {
                      setRoleForm({ name: "", description: "", clone_role_id: "" });
                      setRoleModal(true);
                    }}
                    className="hidden lg:inline-flex"
                  >
                    {t("btn_create_role")}
                  </Btn>
                )}

                {isDirty && canEditRBAC && (
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Btn sm variant="secondary" onClick={resetMatrix} className="flex-1 sm:flex-none">{t("btn_discard")}</Btn>
                    <Btn sm variant="success" onClick={() => setShowConfirmSaveMatrixModal(true)} loading={matrixSaving} className="flex-1 sm:flex-none">{t("btn_save_changes")}</Btn>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bulk Update Controls Bar */}
          {hasRbacFeature && canEditRBAC && (
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 mb-4 p-3 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 text-xs">
              <span className="font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-1">
                <Icon name="zap" size={13} /> {t("btn_bulk_update")}:
              </span>
              <select
                value={bulkTargetRole}
                onChange={e => setBulkTargetRole(e.target.value)}
                className="text-xs border border-indigo-200 dark:border-indigo-800 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-900 text-ink dark:text-slate-100"
              >
                <option value="">All Custom & Admin Roles</option>
                {matrixRoles.filter(r => r.slug !== 'super_admin').map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>

              <select
                value={bulkLevel}
                onChange={e => setBulkLevel(e.target.value)}
                className="text-xs border border-indigo-200 dark:border-indigo-800 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-900 text-ink dark:text-slate-100"
              >
                <option value="full_access">{t("access_full")}</option>
                <option value="read_access">{t("access_read")}</option>
                <option value="entry_access">{t("access_entry")}</option>
                <option value="no_access">{t("access_none")}</option>
              </select>

              <Btn xs variant="primary" onClick={() => handleBulkUpdate(bulkTargetRole, bulkLevel)} className="w-full sm:w-auto">Apply to All Modules</Btn>
            </div>
          )}

          {!hasRbacFeature ? (
            <div className="py-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800/60 rounded-2xl flex flex-col items-center justify-center">
               <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                 <Icon name="lock" size={20} className="text-slate-400 dark:text-slate-500" />
               </div>
               <h3 className="text-sm font-bold text-ink dark:text-slate-200 mb-1">Upgrade to unlock RBAC</h3>
               <p className="text-xs text-muted dark:text-slate-400 mb-4 max-w-sm mx-auto">Role-Based Access Control is not available on your current plan. Upgrade your subscription to manage fine-grained permissions.</p>
               <Btn variant="primary" onClick={() => onNav && onNav('billing')}>Upgrade Plan</Btn>
            </div>
          ) : (
            <>
              <div className="text-[11px] text-muted dark:text-slate-400 flex items-center gap-1.5 mb-2.5 lg:hidden bg-slate-50 dark:bg-slate-800/60 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                <Icon name="info" size={13} className="text-primary flex-shrink-0" />
                <span>Swipe table horizontally to configure permissions for each role.</span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800">
                <DataTable
                loading={matrixLoading}
                cols={[
                  <div className="flex items-center justify-between gap-1">
                    <span>{t("col_module")}</span>
                  </div>,
                  ...matrixRoles.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-2 min-w-[130px]">
                      <div className="flex flex-col text-left">
                        <span className="font-semibold text-ink dark:text-slate-200">{r.name}</span>
                        {!r.is_system && <span className="text-[10px] text-indigo-500 dark:text-indigo-400 font-normal">Custom</span>}
                      </div>
                      {canEditRBAC && r.slug !== 'super_admin' && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleSelectAllForRole(r.id, 'full_access')}
                            title="Select Full Access for all modules"
                            className="p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors text-[10px] font-bold"
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setResetRoleTarget(r);
                            }}
                            title={t("title_reset_role_perms")}
                            className="p-1 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                          >
                            <Icon name="refresh" size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ]}
                rows={matrixModules
                  .filter(mod => mod.toLowerCase().includes(matrixSearch.toLowerCase()))
                  .map(mod => [
                    <div className="flex items-center justify-between gap-2">
                      <span className="capitalize font-medium text-ink">{mod.replace(/_/g, ' ')}</span>
                      {canEditRBAC && (
                        <button
                          type="button"
                          onClick={() => handleSelectAllForModule(mod, 'full_access')}
                          title="Set Full Access across all roles for this module"
                          className="text-[10px] text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 font-bold bg-transparent border-0 cursor-pointer"
                        >
                          All
                        </button>
                      )}
                    </div>,
                    ...matrixRoles.map(role => {
                      const currentLevel = matrixData[mod]?.[role.id] || 'no_access';
                      const isSuperAdmin = role.slug === 'super_admin';

                      if (isSuperAdmin) {
                        return (
                          <span className="px-2 py-1 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 font-semibold rounded-md border border-indigo-100 dark:border-indigo-800 flex items-center justify-center w-24">
                            {t("access_full_system")}
                          </span>
                        );
                      }

                      if (!canEditRBAC) {
                        const levelLabels = {
                          full_access: t("access_full"),
                          read_access: t("access_read"),
                          entry_access: t("access_entry"),
                          no_access: t("access_none")
                        };
                        return (
                          <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                            {levelLabels[currentLevel] || t("access_none")}
                          </span>
                        );
                      }

                      return (
                        <select
                          value={currentLevel}
                          onChange={(e) => handleCellChange(mod, role.id, e.target.value)}
                          className="text-xs border border-gray-200 dark:border-slate-700 rounded-md p-1 bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium w-24 text-gray-700 dark:text-slate-100 cursor-pointer hover:border-gray-300 dark:hover:border-slate-600"
                        >
                          <option value="full_access">{t("access_full")}</option>
                          <option value="read_access">{t("access_read")}</option>
                          <option value="entry_access">{t("access_entry")}</option>
                          <option value="no_access">{t("access_none")}</option>
                        </select>
                      );
                    })
                  ])}
              />
            </div>
            </>
          )}
        </Card>
        )}
      </div>
      
      {/* FAB for Mobile */}
      {hasPermission('users.create') && activeTab === 0 && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => {
              if (adminLimit !== -1 && stats.admins >= adminLimit) {
                toastError(`Admin limit reached (${stats.admins}/${adminLimit}). Please upgrade your plan.`);
              } else {
                setEditUser(null);
                setPhotoPreview(null);
                setForm(def);
                setModal(true);
              }
            }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.18)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
            aria-label={t("btn_add_user")}
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}

      {canEditRBAC && activeTab === 1 && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => {
              setRoleForm({ name: "", description: "", clone_role_id: "" });
              setRoleModal(true);
            }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.18)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
            aria-label={t("btn_create_role")}
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
